import React from 'react';
import { UploadSource } from '../../../components/UploadSource';
import { IioSource } from './sources/IioSource';
import { IioQumuloSource } from './sources/IioQumuloSource';
import { FtpSource, defaultFtpConfig } from './sources/FtpSource';
import { S3Source, defaultS3Config } from './sources/S3Source';
import { ImapProviderSelect, defaultImapConfig, type ImapConfig } from './sources/ImapSource';
import { DropboxSource, defaultDropboxConfig } from './sources/DropboxSource';
import { ForensicSource } from './sources/ForensicSource';
import type { ForensicImageType } from './hooks/useIioListing';

export type SourceType = 'upload' | 'iio' | 'iio_qumulo' | 'ftp' | 'sftp' | 's3' | 's3_qumulo' | 'imap' | 'dropbox' | 'forensicimage';

// One button per Angular tab. S3 and S3 Qumulo share a single tab (the AWS/Qumulo
// switch lives inside the S3 form); FTP/SFTP likewise share one (secure toggle in-form).
// `orgOnly` tabs render only for organization projects. `activeWhen` lists every source
// type that should light up the button.
interface SourceButton { type: SourceType; label: string; icon: string; orgOnly?: boolean; activeWhen?: SourceType[]; testId: string; }
const SOURCE_BUTTONS: SourceButton[] = [
  { type: 'upload',        label: 'Upload',         icon: 'fas fa-upload', testId: 'add-collection-source-upload-btn' },
  { type: 'iio',           label: 'IIO Drive',      icon: 'fas fa-cloud', testId: 'add-collection-source-iio-drive-btn' },
  { type: 'iio_qumulo',    label: 'Client Storage', icon: 'fas fa-cloud', orgOnly: true, testId: 'add-collection-source-client-storage-btn' },
  { type: 'ftp',           label: 'FTP',            icon: 'fas fa-sitemap', activeWhen: ['ftp', 'sftp'], testId: 'add-collection-source-ftp-btn' },
  { type: 's3',            label: 'S3',             icon: 'fas fa-globe', activeWhen: ['s3', 's3_qumulo'], testId: 'add-collection-source-s3-btn' },
  { type: 'imap',          label: 'EMAIL',          icon: 'fas fa-envelope', testId: 'add-collection-source-email-btn' },
  { type: 'dropbox',       label: 'DropBox',        icon: 'fab fa-dropbox', testId: 'add-collection-source-dropbox-btn' },
  { type: 'forensicimage', label: 'Forensic Image', icon: 'fas fa-fingerprint', testId: 'add-collection-source-forensic-btn' },
];

export interface SourceState {
  sourceType: SourceType | null;
  ftpConfig: ReturnType<typeof defaultFtpConfig>;
  s3Config: ReturnType<typeof defaultS3Config> & { endpoint?: string };
  imapConfig: ImapConfig;
  dropboxConfig: ReturnType<typeof defaultDropboxConfig>;
  forensicImageType: ForensicImageType;
  selectedPath: string | null;
  selectedFile: File | null;
}

export function defaultSourceState(): SourceState {
  return {
    sourceType: 'upload',
    ftpConfig: defaultFtpConfig(),
    s3Config: defaultS3Config(),
    imapConfig: defaultImapConfig(),
    dropboxConfig: defaultDropboxConfig(),
    forensicImageType: 'cellebrite',
    selectedPath: null,
    selectedFile: null,
  };
}

export function getSourceConfig(state: SourceState): Record<string, any> {
  switch (state.sourceType) {
    case 'upload':
      return { type: 'upload', valid: state.selectedFile ? 'valid' : '' };
    case 'iio':
      return { type: 'iio', path: state.selectedPath, baseDir: '' };
    case 'iio_qumulo':
      return { type: 'iio_qumulo', path: state.selectedPath, baseDir: '' };
    case 'ftp':
      return { type: 'ftp', ...state.ftpConfig, path: state.selectedPath };
    case 'sftp':
      return { type: 'sftp', ...state.ftpConfig, path: state.selectedPath };
    case 's3':
      return { type: 's3', ...state.s3Config, path: state.selectedPath };
    case 's3_qumulo':
      return { type: 's3_qumulo', ...state.s3Config, path: state.selectedPath };
    case 'imap':
      return { type: 'imap', ...state.imapConfig };
    case 'dropbox':
      return { type: 'dropbox', ...state.dropboxConfig, path: state.selectedPath, baseDir: '' };
    case 'forensicimage':
      return { type: 'forensicimage', forensicImageType: state.forensicImageType, path: state.selectedPath, baseDir: '' };
    default:
      return {};
  }
}

export function isSourceValid(state: SourceState): boolean {
  switch (state.sourceType) {
    case 'upload':        return !!state.selectedFile;
    case 'iio':
    case 'iio_qumulo':
    case 'forensicimage': return !!state.selectedPath;
    case 'ftp':
    case 'sftp':          return !!state.selectedPath && !!state.ftpConfig.hostname;
    case 's3':            return !!state.selectedPath && !!state.s3Config.bucketName;
    case 's3_qumulo':     return !!state.selectedPath && !!state.s3Config.bucketName && !!state.s3Config.endpoint;
    case 'imap':          return !!state.imapConfig.provider && !!state.imapConfig.username && !!state.imapConfig.password;
    case 'dropbox':       return !!state.selectedPath && !!state.dropboxConfig.accessToken;
    default:              return false;
  }
}

interface Props {
  projectId: string;
  collectionName: string;
  isOrganizationProject: boolean;
  state: SourceState;
  onChange: (state: SourceState) => void;
  saving: boolean;
  uploading?: boolean;
  uploadProgress?: number;
  uploadBytesLoaded?: number;
  uploadBytesTotal?: number;
}

export function Step2SourceType({
  projectId, collectionName, isOrganizationProject, state, onChange, saving,
  uploading, uploadProgress, uploadBytesLoaded, uploadBytesTotal,
}: Props) {
  const set = (updates: Partial<SourceState>) => onChange({ ...state, ...updates });

  const selectType = (type: SourceType) =>
    set({ sourceType: type, selectedPath: null, selectedFile: null });

  const visibleButtons = SOURCE_BUTTONS.filter(b => !b.orgOnly || isOrganizationProject);

  return (
    <div>
      <h4>Where should we fetch the data for <strong>{collectionName}</strong>?</h4>

      <div className="btn-group" role="group" style={{ marginBottom: 20 }} data-testid="add-collection-source-list">
        {visibleButtons.map(b => {
          const active = (b.activeWhen ?? [b.type]).includes(state.sourceType!);
          return (
            <button
              key={b.type}
              type="button"
              className={`btn btn-default${active ? ' active' : ''}`}
              onClick={() => selectType(b.type)}
              disabled={saving}
              data-testid={b.testId}
            >
              <i className={`${b.icon} fa-fw`} aria-hidden="true" style={{ marginRight: 4 }} />
              {b.label}
            </button>
          );
        })}
      </div>

      {state.sourceType === 'upload' && (
        <UploadSource
          file={state.selectedFile}
          onChange={file => set({ selectedFile: file })}
          disabled={saving}
          uploading={uploading}
          uploadProgress={uploadProgress}
          uploadBytesLoaded={uploadBytesLoaded}
          uploadBytesTotal={uploadBytesTotal}
          testId="add-collection-upload-panel"
        />
      )}

      {(state.sourceType === 'iio') && (
        <IioSource
          projectId={projectId}
          selectedPath={state.selectedPath}
          onPathChange={path => set({ selectedPath: path })}
          disabled={saving}
          testId="add-collection-iio-browser-panel"
        />
      )}

      {state.sourceType === 'iio_qumulo' && (
        <IioQumuloSource
          projectId={projectId}
          selectedPath={state.selectedPath}
          onPathChange={path => set({ selectedPath: path })}
          disabled={saving}
        />
      )}

      {(state.sourceType === 'ftp' || state.sourceType === 'sftp') && (
        <FtpSource
          config={state.ftpConfig}
          onChange={cfg => set({ ftpConfig: cfg, sourceType: cfg.secure ? 'sftp' : 'ftp' })}
          selectedPath={state.selectedPath}
          onPathChange={path => set({ selectedPath: path })}
          disabled={saving}
        />
      )}

      {(state.sourceType === 's3' || state.sourceType === 's3_qumulo') && (
        <S3Source
          config={state.s3Config}
          onChange={cfg => set({ s3Config: cfg })}
          qumulo={state.sourceType === 's3_qumulo'}
          onToggleQumulo={q => set({ sourceType: q ? 's3_qumulo' : 's3', selectedPath: null })}
          selectedPath={state.selectedPath}
          onPathChange={path => set({ selectedPath: path })}
          disabled={saving}
        />
      )}

      {state.sourceType === 'imap' && (
        <ImapProviderSelect
          config={state.imapConfig}
          onChange={cfg => set({ imapConfig: cfg })}
          disabled={saving}
        />
      )}

      {state.sourceType === 'dropbox' && (
        <DropboxSource
          config={state.dropboxConfig}
          onChange={cfg => set({ dropboxConfig: cfg })}
          selectedPath={state.selectedPath}
          onPathChange={path => set({ selectedPath: path })}
          disabled={saving}
        />
      )}

      {state.sourceType === 'forensicimage' && (
        <ForensicSource
          projectId={projectId}
          forensicImageType={state.forensicImageType}
          onChange={type => set({ forensicImageType: type })}
          selectedPath={state.selectedPath}
          onPathChange={path => set({ selectedPath: path })}
          disabled={saving}
        />
      )}
    </div>
  );
}
